import { collection, getDocs, setDoc, doc, deleteDoc, Timestamp, updateDoc, getDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { checkAndUpdateOrdersStock } from './stockUpdates';
import { syncWooCommerceOrders } from '../../services/woocommerceService';
import { 
  checkNewMessages,
  checkNewOrders,
  checkLowStockProducts,
  checkUnprocessedSales,
  checkPendingPayments,
  checkSalesGoals,
  checkNewQuoteRequests
} from './notificationChecks';

// Armazenar os IDs dos intervalos para limpeza posterior
let intervalIds: { [key: string]: NodeJS.Timeout } = {};

const defaultJobs = [
  {
    id: 'stock-update',
    name: 'Atualizar Stock das Encomendas',
    handler: checkAndUpdateOrdersStock,
    interval: 15 * 60 * 1000 // 15 minutos
  },
  // Removed sync-woocommerce-orders job to prevent automatic syncing
  {
    id: 'new-messages',
    name: 'Verificar Novas Mensagens',
    handler: checkNewMessages,
    interval: 5 * 60 * 1000 // 5 minutos
  },
  {
    id: 'quote-requests',
    name: 'Verificar Pedidos de Orçamento',
    handler: checkNewQuoteRequests,
    interval: 15 * 60 * 1000 // 15 minutos
  },
  {
    id: 'new-orders',
    name: 'Verificar Novos Pedidos',
    handler: checkNewOrders,
    interval: 10 * 60 * 1000 // 10 minutos
  },
  {
    id: 'pending-payments',
    name: 'Verificar Pagamentos Pendentes',
    handler: checkPendingPayments,
    interval: 20 * 60 * 1000 // 20 minutos
  },
  {
    id: 'sales-goals',
    name: 'Verificar Metas de Vendas',
    handler: checkSalesGoals,
    interval: 60 * 60 * 1000 // 1 hora
  },
  {
    id: 'low-stock',
    name: 'Verificar Produtos com Estoque Baixo',
    handler: checkLowStockProducts,
    interval: 30 * 60 * 1000 // 30 minutos
  },
  {
    id: 'unprocessed-sales',
    name: 'Verificar Vendas Não Processadas',
    handler: checkUnprocessedSales,
    interval: 15 * 60 * 1000 // 15 minutos
  }
];

// Função para limpar intervalos existentes
function clearExistingIntervals() {
  Object.values(intervalIds).forEach(clearInterval);
  intervalIds = {};
}

// Função para configurar um novo intervalo
function setupInterval(job: typeof defaultJobs[0]) {
  if (intervalIds[job.id]) {
    clearInterval(intervalIds[job.id]);
  }
  
  intervalIds[job.id] = setInterval(async () => {
    await runJob(job.id, job.handler);
  }, job.interval);
}

async function updateJobStatus(jobId: string, status: string, error?: any) {
  const batch = writeBatch(db);
  const jobRef = doc(db, 'cronJobs', jobId);
  
  const now = Timestamp.now();
  const job = defaultJobs.find(j => j.id === jobId);
  const nextRun = job ? new Date(Date.now() + job.interval) : null;
  
  const updateData = {
    id: jobId,
    taskId: jobId, // Adicionando taskId para garantir consistência
    name: job?.name,
    status,
    lastRun: now,
    nextRun: nextRun ? Timestamp.fromDate(nextRun) : null,
    error: error ? error.message : null
  };

  batch.set(jobRef, updateData, { merge: true });
  await batch.commit();
}

async function runJob(jobId: string, handler: () => Promise<void>) {
  const job = defaultJobs.find(j => j.id === jobId);
  if (!job) return;

  try {
    // Atualizar status para running
    await updateJobStatus(jobId, 'running');
    
    // Executar a tarefa
    await handler();
    
    // Atualizar status para completed e próxima execução
    await updateJobStatus(jobId, 'completed');
  } catch (error) {
    console.error(`Erro ao executar tarefa ${jobId}:`, error);
    await updateJobStatus(jobId, 'failed', error);
  }
}

export async function initializeCronJobs(
  onProgress?: (jobName: string, current: number, total: number) => void
) {
  // Limpar intervalos existentes
  clearExistingIntervals();

  try {
    // Executar todas as tarefas em paralelo na primeira vez
    const totalJobs = defaultJobs.length;
    await Promise.all(
      defaultJobs.map(async (job, index) => {
        if (onProgress) {
          onProgress(job.name, index + 1, totalJobs);
        }
        
        // Atualizar status para 'running'
        await updateJobStatus(job.id, 'running');
        
        // Executar a tarefa
        await runJob(job.id, job.handler);
        
        // Configurar o intervalo para execuções futuras
        setupInterval(job);
      })
    );
  } catch (error) {
    console.error('Erro ao inicializar tarefas:', error);
    throw error;
  }
}
